import { useRef } from 'react';
import { Link } from 'react-router';
import { countUp, gsap, useGSAP, EASE } from '@/lib/motion';
import type { TournamentSummary } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface FeaturedTournamentsProps {
  tournaments: TournamentSummary[];
  loading?: boolean;
}

export function FeaturedTournaments({ tournaments, loading = false }: FeaturedTournamentsProps) {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      if (tournaments.length === 0 && !loading) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
          once: true,
        },
        defaults: { ease: EASE.signature },
      });

      tl.from('[data-anim="section-header"]', { y: 24, autoAlpha: 0, duration: 0.7 })
        .from(
          '[data-anim="card"]',
          { y: 36, autoAlpha: 0, stagger: 0.12, duration: 0.8, ease: EASE.signature },
          '-=0.35',
        );

      gsap.utils.toArray<HTMLElement>('[data-count]').forEach((el, index) => {
        tl.add(
          countUp(el, Number(el.dataset['count'] ?? 0), { duration: 1.0 }),
          index === 0 ? '-=0.4' : '<0.1',
        );
      });
    },
    {
      scope: root,
      dependencies: [tournaments.map((t) => t.id).join(',')],
      revertOnUpdate: true,
    },
  );

  return (
    <section ref={root} className="relative z-10 border-t border-line bg-void/60 py-20">
      <div className="mx-auto max-w-ucc px-6">
        <header data-anim="section-header" className="mb-10 flex items-baseline justify-between">
          <div>
            <span className="ucc-eyebrow">Temporada 2026</span>
            <h2 className="ucc-display mt-2 text-3xl tracking-wide text-paper md:text-4xl">
              Inscrições Abertas
            </h2>
          </div>
          <Link
            to="/campeonatos"
            className="group flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-gold-ink"
          >
            ver todos os campeonatos
            <span className="transition-transform duration-[--duration-quick] group-hover:translate-x-1">→</span>
          </Link>
        </header>

        <div className="grid gap-6 md:grid-cols-2">
          {loading ? (
            <>
              <TournamentCardSkeleton />
              <TournamentCardSkeleton />
            </>
          ) : tournaments.length === 0 ? (
            <div className="md:col-span-2">
              <EmptyState />
            </div>
          ) : (
            tournaments
              .slice(0, 2)
              .map((tournament) => <TournamentCard key={tournament.id} tournament={tournament} />)
          )}
        </div>
      </div>
    </section>
  );
}

function TournamentCard({ tournament }: { tournament: TournamentSummary }) {
  const { isStaff } = useAuth();
  const full = tournament.spotsLeft === 0;
  const almostFull = tournament.spotsLeft > 0 && tournament.spotsLeft <= 3;
  const filled = tournament.capacity - tournament.spotsLeft;
  const pct = Math.round((filled / tournament.capacity) * 100);

  return (
    <article
      data-anim="card"
      className="ucc-gold-border group relative overflow-hidden rounded-xl p-6 transition-all duration-[--duration-standard] hover:-translate-y-1.5 hover:shadow-[0_16px_36px_-10px_rgba(248,212,40,0.2)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="ucc-eyebrow">{tournament.game === 'fc26' ? 'EA FC 26' : 'EA FC 27'}</p>
          <h3 className="ucc-display mt-1.5 text-2xl text-paper">{tournament.name}</h3>
          <p className="mt-1 text-sm text-muted">{formatDateTime(tournament.startsAt)}</p>
        </div>
        <PrizeBadge cents={tournament.prizePoolCents} />
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-muted">
            <span data-count={filled} className="tabular font-semibold text-paper">
              0
            </span>
            <span className="text-faint"> / {tournament.capacity} vagas</span>
          </span>
          <span
            className={
              full
                ? 'font-semibold text-loss'
                : almostFull
                  ? 'font-semibold text-gold-ink'
                  : 'text-faint'
            }
          >
            {full
              ? 'Lotado'
              : almostFull
                ? `Últimas ${tournament.spotsLeft}`
                : `${tournament.spotsLeft} disponíveis`}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-3">
          <div
            className="h-full rounded-full bg-linear-to-r from-gold-deep to-gold transition-[width] duration-[--duration-hero] ease-[--ease-signature]"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={filled}
            aria-valuemin={0}
            aria-valuemax={tournament.capacity}
            aria-label={`${filled} de ${tournament.capacity} vagas preenchidas`}
          />
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        {/* Ver o comentário na página do campeonato: staff não se inscreve. */}
        {!isStaff && (
        <Link
          to={`/campeonatos/${tournament.slug}/inscricao`}
          aria-disabled={full}
          className={
            full
              ? 'pointer-events-none flex-1 rounded-lg border border-line bg-surface-2 py-3 text-center text-sm font-semibold text-faint uppercase'
              : 'flex-1 rounded-lg bg-gold py-3 text-center text-sm font-semibold text-black uppercase transition-all duration-[--duration-quick] hover:bg-gold-bright hover:shadow-[var(--shadow-gold)] hover:scale-[1.01]'
          }
        >
          {full ? 'Vagas esgotadas' : `Inscrever — ${formatMoney(tournament.entryFeeCents)}`}
        </Link>
        )}
        <Link
          to={`/campeonatos/${tournament.slug}`}
          className={
            isStaff
              ? 'flex-1 rounded-lg border border-line py-3 text-center text-sm font-medium text-muted transition-colors duration-[--duration-quick] hover:border-gold/50 hover:text-paper'
              : 'rounded-lg border border-line px-5 py-3 text-sm font-medium text-muted transition-colors duration-[--duration-quick] hover:border-gold/50 hover:text-paper'
          }
        >
          Detalhes
        </Link>
      </div>
    </article>
  );
}

function PrizeBadge({ cents }: { cents: number }) {
  if (cents <= 0) return null;
  return (
    <div className="shrink-0 rounded-lg border border-gold/25 bg-gold/8 px-3 py-2 text-right">
      <p className="text-[0.625rem] font-medium tracking-wider text-gold-ink/80 uppercase">Premiação</p>
      <p className="ucc-display tabular text-xl text-gold-ink">{formatMoney(cents)}</p>
    </div>
  );
}

function TournamentCardSkeleton() {
  return (
    <div
      data-anim="card"
      className="ucc-panel h-[13.5rem] animate-pulse rounded-xl"
      aria-hidden
    />
  );
}

function EmptyState() {
  return (
    <div data-anim="card" className="ucc-panel p-8 text-center">
      <p className="text-paper">Nenhuma inscrição aberta no momento.</p>
      <p className="mt-1.5 text-sm text-muted">
        A próxima edição é anunciada no Discord da UCC assim que as datas fecham.
      </p>
    </div>
  );
}

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

function formatDateTime(epochSeconds: number | null): string {
  if (!epochSeconds) return 'Data a definir';
  return new Date(epochSeconds * 1000)
    .toLocaleString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(',', ' ·');
}
